// Docit Work — the wire shapes of `work.*` (09 §2): WorkItemCard, WorkItemFull, comments, events,
// links. Bespoke envelopes (`{ pagination, data, facets, policies }` / `{ data, policies }`); the
// scenes validate lightly and render what came back — the server owns every rule.

export type WorkKind =
  | "epic"
  | "feature"
  | "story"
  | "task"
  | "bug"
  | "issue"
  | "question"
  | "spike"
  | "incident";
export type WorkState =
  | "new"
  | "triaged"
  | "planned"
  | "in_progress"
  | "in_review"
  | "done"
  | "cancelled";
export type WorkStateCategory = "proposed" | "committed" | "active" | "completed" | "removed";

export const WORK_KINDS: WorkKind[] = [
  "epic",
  "feature",
  "story",
  "task",
  "bug",
  "issue",
  "question",
  "spike",
  "incident",
];
export const WORK_STATES: WorkState[] = [
  "new",
  "triaged",
  "planned",
  "in_progress",
  "in_review",
  "done",
  "cancelled",
];
export const WORK_PRIORITIES = ["none", "low", "medium", "high", "critical"] as const;
export const WORK_SEVERITIES = ["s1", "s2", "s3", "s4"] as const;
export const DONE_RESOLUTIONS = ["fixed", "done", "answered", "merged"] as const;
export const CANCELLED_RESOLUTIONS = [
  "duplicate",
  "wont_fix",
  "cannot_reproduce",
  "obsolete",
  "invalid",
  "moved",
] as const;

/** The next states a tile may move to from each state (01 §4.2, default profile; the server re-judges). */
export const NEXT_STATES: Record<WorkState, WorkState[]> = {
  new: ["triaged", "cancelled"],
  triaged: ["planned", "in_progress", "cancelled"],
  planned: ["in_progress", "triaged", "cancelled"],
  in_progress: ["in_review", "cancelled"],
  in_review: ["done", "in_progress", "cancelled"],
  done: ["in_progress"],
  cancelled: ["triaged"],
};

export interface PersonRef {
  id: string;
  displayName: string | null;
  avatarUrl: string | null;
}

export interface WorkAssignee {
  kind: "none" | "user" | "thinker" | "role";
  id: string | null;
  displayName: string | null;
  avatarUrl: string | null;
}

export interface WorkTether {
  userId: string | null;
  delegateUserId: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  inherited: boolean;
  setBy: string;
  inheritedFrom: string | null;
}

export interface WorkItemCard {
  id: string;
  key: string;
  projectId: string;
  kind: WorkKind;
  title: string;
  state: WorkState;
  stateCategory: WorkStateCategory;
  resolution: string | null;
  priority: string;
  severity: string | null;
  estimate: number | null;
  /** Always an object from the presenter today; typed nullable so every reader stays null-safe (2026-09-26). */
  assignee: WorkAssignee | null;
  tetherer: WorkTether | null;
  labels: string[];
  area: string | null;
  sprint: { id: string; name: string | null } | null;
  parent: { id: string; key: string; title: string; kind: string } | null;
  blocked: {
    isBlocked: boolean;
    reason: string | null;
    byRef: { kind: string; id: string | null } | null;
  };
  dueAt: string | null;
  createdAt: string;
  updatedAt: string;
  counts: { children: number; openQuestions: number; comments: number };
  /**
   * Who/what set the CURRENT state, from the history: a run (`byRun`) or a person/agent by hand (`setByHand`).
   * `note` is plain text, present only where it matters (an active/review tile no run backs). Absent on older servers.
   */
  stateProvenance?: {
    setBy: { kind: string; id: string | null; nodeId?: string | null };
    channel: string;
    at: string;
    byRun: boolean;
    setByHand: boolean;
    runBound: boolean;
    note: string | null;
  } | null;
  version: number;
}

/** The plain note for a state no run backs (active/review, set by hand, no run ever bound) — else null. */
export function unbackedStateNote(card: Pick<WorkItemCard, "stateProvenance" | "stateCategory">): string | null {
  const p = card.stateProvenance;
  return p && p.setByHand && !p.runBound && card.stateCategory === "active" ? p.note : null;
}

export interface WorkActor {
  kind: string;
  id: string;
  displayName: string | null;
  avatarUrl: string | null;
}

export interface WorkComment {
  id: string;
  itemId: string;
  body: string;
  tag: string | null;
  author: WorkActor;
  channel: string;
  editedAt: string | null;
  createdAt: string;
}

export interface WorkEvent {
  id: number;
  seq: number;
  kind: string;
  actor: { kind: string; id: string };
  channel: string;
  payload: Record<string, unknown> | null;
  at: string;
}

export interface WorkLink {
  id: string;
  sourceItemId: string;
  type: string;
  target: {
    kind: string;
    id?: string;
    key?: string | null;
    title?: string | null;
    itemKind?: string | null;
    url?: string | null;
  };
  createdAt: string;
  inbound?: boolean;
}

export interface WorkItemFull {
  card: WorkItemCard;
  description: string | null;
  effortHours: number | null;
  dates: {
    startedAt: string | null;
    dueAt: string | null;
    completedAt: string | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
  reporter: WorkActor | null;
  createdBy: WorkActor | null;
  watchers: PersonRef[];
  links: WorkLink[];
  children: WorkItemCard[] | null;
  questions: WorkItemCard[] | null;
  comments: WorkComment[] | null;
  history: WorkEvent[] | null;
  policies: { id: string; abilities: Record<string, boolean> }[];
  treeVersion: number;
  /** Where the tile came from; a plan-made tile carries `{ kind: "plan", planRef, ref }` (19 §2). */
  origin?: { kind?: string; planRef?: string | null; ref?: string | null } | null;
}

export interface WorkFacets {
  kind: Record<string, number>;
  state: Record<string, number>;
  assignee: Record<string, number>;
  label: Record<string, number>;
  area: Record<string, number>;
  sprint: Record<string, number>;
}

export interface WorkListPage {
  pagination: { offset: number; limit: number; total: number };
  data: WorkItemCard[];
  facets: WorkFacets | null;
}

export interface WorkProject {
  id: string;
  name: string;
  status: string;
  ownerId: string | null;
  keyPrefix: string;
  configured: boolean;
  openItems: number;
}

export interface WorkError {
  ok: false;
  error: string;
  message?: string;
  current?: WorkItemCard;
}

/** Pulls the server's `{ ok:false, error, message }` out of the adapter's thrown error, or falls back. */
export function workErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const body = error as { body?: unknown; payload?: unknown; message?: unknown };
    const candidate = (body.body ?? body.payload) as Partial<WorkError> | undefined;
    if (candidate && typeof candidate === "object") {
      if (candidate.error === "stale")
        return "Someone changed this tile while you were editing — reloaded.";
      if (typeof candidate.message === "string" && candidate.message)
        return `${candidate.error}: ${candidate.message}`;
      if (typeof candidate.error === "string") return candidate.error;
    }
    if (typeof body.message === "string" && body.message) return body.message;
  }
  return fallback;
}

export function isStale(error: unknown): boolean {
  const body = ((error as { body?: unknown })?.body ?? (error as { payload?: unknown })?.payload) as
    | Partial<WorkError>
    | undefined;
  return (
    body?.error === "stale" ||
    /409|stale/i.test(String((error as { message?: string })?.message ?? ""))
  );
}

export const KIND_GLYPH: Record<WorkKind, string> = {
  epic: "◆",
  feature: "▲",
  story: "▣",
  task: "☐",
  bug: "●",
  issue: "◌",
  question: "?",
  spike: "⚡",
  incident: "▲!",
};

/**
 * Weave (15 §6.2): state → category. The categories are the cross-system common ground AND the
 * one colour language every Weave surface uses — a chip coloured by kind would make the reader
 * decode two scales at once. The server sends `stateCategory`; this is the fallback for shapes
 * that carry only the state.
 */
export const CATEGORY_OF_STATE: Record<WorkState, WorkStateCategory> = {
  new: "proposed",
  triaged: "proposed",
  planned: "committed",
  in_progress: "active",
  in_review: "active",
  done: "completed",
  cancelled: "removed",
};

export const STATE_LABEL: Record<WorkState, string> = {
  new: "New",
  triaged: "Backlog",
  planned: "Planned",
  in_progress: "In progress",
  in_review: "In review",
  done: "Done",
  cancelled: "Cancelled",
};
